import { isIfAnyoneBuildsItFrontPage } from '@/components/seasonal/styles';

export const hideSpoilers = (theme: ThemeType) => ({
  backgroundColor: theme.palette.panelBackground.spoilerBlock,
  color: theme.palette.panelBackground.spoilerBlock,
  '& a, & a:hover, & a:focus, & a::after, & li': {
    color: theme.palette.panelBackground.spoilerBlock
  },
  '& code': {
    backgroundColor: theme.palette.panelBackground.spoilerBlock,
  },
  '& img, & video, & iframe, & svg, & canvas': {
    opacity: 0,
  },
});

const spoilerStyles = (theme: ThemeType) => ({
  '& p.spoiler': {
    margin: 0,
  },
  '& .spoiler': {
    padding: 8,
    pointerEvents: 'auto',
    minHeight: 15,
    '&:not(:hover)': { // using ':not(:hover)' means we don't need to manually reset elements with special colors or backgrounds, instead they just automatically stay the same if we're not hovering
      ...hideSpoilers(theme),
    }
  },
  // Note: ".spoiler" is the old class Oli originally used. ".spoilers" is a new class 
  // that is applied in make_editable_callbacks.js to groups of adjaecent spoiler paragraphs.
  // (see the make_editable_callbacks.js file for details)
  '& div.spoilers': {
    margin: '1em 0',
    overflow: 'auto',
    '&:not(:hover)': {
      ...hideSpoilers(theme),
    },
    '&:hover': {
      background: theme.palette.panelBackground.revealedSpoilerBlock, // This leaves a light grey background over the revealed-spoiler to make it more obvious where it started.
    },
    '& > p' : {
      margin: '0 !important',
      padding: '0.5em 8px !important'
    },
  },
  '& p.spoiler-v2': {
    margin: 0,
    padding: '0.5em 8px'
  },
  '& .spoilers:not(:hover)::selection, & .spoilers:not(:hover) ::selection': {
    backgroundColor: 'transparent'
  },
  '& .spoilers > p:hover ~ p': {
    ...hideSpoilers(theme),
  }
})

const metaculusPreviewStyles = (theme: ThemeType) => ({
  '& div.metaculus-preview': {
    backgroundColor: theme.palette.panelBackground.metaculusBackground,
    '& iframe': {
      width: '100%',
      height: 400,
      border: 'none'
    }
  }
})

const manifoldPreviewStyles = (_theme: ThemeType) => ({
  "& div.manifold-preview": {
    "& iframe": {
      width: "100%",
      height: 400,
      border: "none",
    },
  },
});

const neuronpediaPreviewStyles = (theme: ThemeType) => ({
  "& div.neuronpedia-preview": {
    "& iframe": {
      width: "100%",
      height: 360,
      maxWidth: 639,
      border: "1px solid",
      borderColor: theme.palette.grey[300],
      borderRadius: 6,
    },
  },
});

export const calendlyPreviewStyles = (theme: ThemeType) => ({
  "& div.calendly-preview": {
    "& iframe": {
      "width": "calc(100% - 10px)",
      "height": 750,
      border: "2px solid",
      borderRadius: 10,
      borderColor: theme.palette.grey[200],
      padding: 0,
      marginRight: 5,
      marginLeft: 5
    },
  },
});

const strawpollPreviewStyles = (_theme: ThemeType) => ({
  "& div.strawpoll-embed": {
    "& iframe": {
      width: "100%",
      height: 400,
      border: "none",
    },
  },
});

const metaforecastPreviewStyles = (_theme: ThemeType) => ({
  "& div.metaforecast-preview": {
    "& iframe": {
      width: "100%",
      height: 400,
      border: "none",
    },
  },
});

const owidPreviewStyles = (_theme: ThemeType) => ({
  '& div.owid-preview': {
    '& iframe': {
      width: '100%',
      height: 400,
      border: 'none'
    }
  }
})

const estimakerPreviewStyles = (_theme: ThemeType) => ({
  '& div.estimaker-preview': {
    display: 'flex',
    '& iframe': {
      width: '100%',
      height: 400,
      border: 'none'
    }
  }
})

const viewpointsPreviewStyles = (_theme: ThemeType) => ({
  '& div.viewpoints-preview': {
    display: 'flex',
    '& iframe': {
      width: '100%',
      height: 300,
      border: 'none'
    }
  }
})

const youtubePreviewStyles = (_theme: ThemeType) => ({
  '& figure.media div[data-oembed-url*="youtube.com"], & figure.media div[data-oembed-url*="youtu.be"], & div.youtube-preview': {
    position: 'relative',
    height: 0,
    paddingBottom: '56.2493%',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      border: 'none'
    }
  }
})

const lwartifactsPreviewStyles = (_theme: ThemeType) => ({
  '& figure.media div[data-oembed-url*="lwartifacts.vercel.app"]': {
    '& iframe': {
      width: '100%',
      height: 525,
      border: 'none'
    }
  },
  '& div.lwartifacts-preview': {
    '& iframe': {
      width: '100%',
      height: 525,
      border: 'none'
    }
  }
})

const tableStyles = (theme: ThemeType) => ({
  borderCollapse: "collapse",
  borderSpacing: 0,
  border: theme.palette.border.table,
  margin: "auto",
  height: "100%",
  textAlign: "left",
  width: '100%',
});

const tableCellStyles = (theme: ThemeType) => ({
  minWidth: "2em",
  padding: ".4em",
  border: `1px double ${theme.palette.grey[320]}`,
  wordBreak: "normal",
  '& p': {
    marginTop: '0.5em',
    marginBottom: '0.5em',
  },
  '& p:first-of-type': {
    marginTop: 0
  }
});

const tableHeadingStyles = (theme: ThemeType) => ({
  background: theme.palette.grey[50],
  fontWeight: 700
});

const hrStyles = (theme: ThemeType) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  margin: "32px 0",
  border: "none", /* strip default hr styling */
  background: "transparent",
  textAlign: "center",
  '&:after': {
    marginLeft: 12,
    color: theme.palette.icon.horizRuleDots,
    fontSize: 13,
    letterSpacing: "12px", /* increase space between dots */
    content: '"•••"',
  }
});

const footnoteStyles = (_theme: ThemeType) => ({
  '& .footnote-item > *': {
    verticalAlign: "text-top",
  },
  '& .footnote-back-link': {
    position: "relative",
    top: "-0.2em",
  },
  '& .footnotes .footnote-back-link > sup': {
    marginRight: 0,
  },
 '& .footnote-content': {
    display: "inline-block",
    padding: "0 0.3em",
    width: '95%',
  },
});

const llmContentBlockStyles = (theme: ThemeType) => ({
  '& .llm-content-block': {
    margin: '1em 0',
    position: 'relative',
    fontFamily: '"cronos-pro", serif',
    fontSize: 19.1,
    fontWeight: 400,
    opacity: 0.94,
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontFamily: 'inherit',
    },
    // Prevent baseBodyStyles selectors (which spread postStyle/body1/commentStyle)
    // from overriding the LLM block's font on these elements. Without this,
    // list items and blockquotes get the post/comment font instead of cronos-pro.
    '& li, & blockquote': {
      fontFamily: 'inherit',
    },
    // Render the model label inline so the content starts immediately after it.
    '&::before': {
      content: 'attr(data-model-name)',
      display: 'inline-block',
      lineHeight: 1.3,
      fontSize: '0.85em',
      color: theme.palette.greyAlpha(0.6),
      paddingRight: 6,
      borderRight: `1px solid ${theme.palette.grey[400]}`,
      fontWeight: 600,
      fontVariant: 'small-caps',
      position: 'relative',
      top: 2
    },
    '&:has(> .llm-content-block-content > p:first-child)::before': {
      float: 'left',
      marginRight: 8,
      marginBottom: 0,
    },
    '&:not(:has(> .llm-content-block-content > p:first-child))::before': {
      float: 'none',
      display: 'block',
      width: 'fit-content',
      marginRight: 0,
      marginTop: '1em',
      marginBottom: '1em',
    },
    '& > .llm-content-block-content': {
      '& > :first-child': {
        marginTop: 0,
      },
      '& > :last-child': {
        marginBottom: 0,
      },
    },
    '&:has(> .llm-content-block-content > p:last-child) > .llm-content-block-content > p:last-child::after': {
      content: '"⊙"',
      color: theme.palette.greyAlpha(0.55),
      fontSize: '0.85em',
      paddingLeft: 3,
      marginLeft: 9,
      borderLeft: `1px solid ${theme.palette.grey[400]}`,
      fontWeight: 300,
      letterSpacing: 2.1,
      opacity: 0.6,
    },
    '& > .llm-content-block-content > p:last-child:has(> br:only-child)': {
      display: 'inline-block',
      margin: 0,
      minWidth: '0.6em',
    },
    '& > .llm-content-block-content > p:last-child:has(> br:only-child) > br:only-child': {
      display: 'none',
    },
    '& > .llm-content-block-content > p:last-child:has(> br:only-child)::after': {
      content: '"⊙"',
      display: 'inline-block',
      color: theme.palette.greyAlpha(0.55),
      fontSize: '0.85em',
      lineHeight: 'inherit',
      paddingLeft: 3,
      marginLeft: 0,
      borderLeft: `1px solid ${theme.palette.grey[400]}`,
      fontWeight: 300,
      letterSpacing: 2.1,
      opacity: 0.6,
    },
    '&:not(:has(> .llm-content-block-content > p:last-child)) > .llm-content-block-content::after': {
      content: '"⊙"',
      display: 'block',
      width: 'fit-content',
      color: theme.palette.greyAlpha(0.55),
      fontSize: '0.85em',
      lineHeight: 1.3,
      marginTop: '1em',
      marginBottom: '1em',
      paddingLeft: 3,
      borderLeft: `1px solid ${theme.palette.grey[400]}`,
      fontWeight: 300,
      letterSpacing: 2.1,
      opacity: 0.6,
    },
  },
});

// Editors hit-test clicks against this: triangle vs. title text.
export const COLLAPSIBLE_MARKER_GUTTER = 22;

const COLLAPSIBLE_MARKER_WIDTH = 6;
const COLLAPSIBLE_MARKER_HEIGHT = 8;

// Drawn with borders rather than as a "▶", which mobile browsers
// substitute with an emoji glyph. `1lh` centers it on the title's
// first line, however many lines or blocks the title has.
const collapsibleMarkerStyles = (theme: ThemeType) => ({
  content: '""',
  position: "absolute",
  left: (COLLAPSIBLE_MARKER_GUTTER - COLLAPSIBLE_MARKER_WIDTH) / 2,
  top: `calc((1lh - ${COLLAPSIBLE_MARKER_HEIGHT}px) / 2)`,
  width: 0,
  height: 0,
  borderStyle: "solid",
  borderWidth: `${COLLAPSIBLE_MARKER_HEIGHT / 2}px 0 ${COLLAPSIBLE_MARKER_HEIGHT / 2}px ${COLLAPSIBLE_MARKER_WIDTH}px`,
  borderColor: `transparent transparent transparent ${theme.palette.grey[600]}`,
  transform: "rotate(0deg)",
  transition: "transform 0.15s ease",
  cursor: "pointer",
});

const collapsibleSectionStyles = (theme: ThemeType) => ({
  '& .detailsBlock': {
    // !important: CkEditor toggles a border here to show selection.
    border: 'none !important',
    marginTop: '.75em',
    marginBottom: '.75em',
  },
  '& .detailsBlockTitle': {
    position: "relative",
    padding: `2px 0 2px ${COLLAPSIBLE_MARKER_GUTTER}px`,

    // !important: CkEditor whitens this when the cursor is inside.
    background: 'transparent !important',

    // Suppress the native <summary> marker in favour of ours.
    listStyle: "none",
    '&::-webkit-details-marker': {
      display: "none",
    },
    '&::marker': {
      content: '""',
    },
    '&::before': {
      ...collapsibleMarkerStyles(theme),
    },
  },
  '& summary.detailsBlockTitle': {
    cursor: "pointer",
  },
  // Half the gutter puts the rule directly under the triangle.
  '& .detailsBlockContent': {
    marginTop: 8,
    marginLeft: COLLAPSIBLE_MARKER_GUTTER / 2,
    paddingLeft: 14,
    borderLeft: `1px solid ${theme.palette.grey[400]}`,
  },
  // !important: the global paragraph margins differ between the
  // editor and the rendered page, so they'd desync the spacing.
  "& .detailsBlockContent > *:first-child, & .detailsBlockTitle > *:first-child": {
    marginTop: '0 !important',
  },
  "& .detailsBlockContent > *:last-child, & .detailsBlockTitle > *:last-child": {
    marginBottom: '0 !important',
  },

  // The editors' <div> form is open unless marked closed.
  '& details.detailsBlock[open] > .detailsBlockTitle::before, & div.detailsBlockTitle::before': {
    transform: "rotate(90deg)",
  },
  '& .detailsBlock.closed div.detailsBlockTitle::before, & .detailsBlockClosed div.detailsBlockTitle::before': {
    transform: "rotate(0deg)",
  },
  "& .detailsBlock.closed .detailsBlockContent, & .detailsBlockClosed .detailsBlockContent": {
    display: "none",
  },

  // Placeholder text in the editor for a collapsible section with no title.
  // CkEditor represents this with a <br> placeholder as:
  //     <p><br data-cke-filler="true"/></p>
  "& .detailsBlockTitle p:has(> br[data-cke-filler=true]:only-child)::after": {
    content: '"Collapsible Section Title"',
    color: theme.palette.greyAlpha(0.3),
    pointerEvents: "none",
    position: "absolute",
    top: 0,
  },
});

const conditionallyVisibleBlockStyles = (theme: ThemeType) => ({
  "& .conditionallyVisibleBlock": {
    border: theme.palette.border.normal,
    borderRadius: 4,
    padding: 8,
    "&.defaultHidden": {
      display: "none",
    },
  },
});

const ctaButtonStyles = (theme: ThemeType) => ({
  '& .ck-cta-button': {
    display: 'block',
    fontFamily: theme.palette.fonts.sansSerifStack,
    minWidth: 30,
    width: 'fit-content',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '20px',
    textTransform: 'none',
    padding: '12px 16px',
    borderRadius: theme.borderRadius.small,
    boxShadow: 'none',
    backgroundColor: theme.palette.buttons.alwaysPrimary,
    color: theme.palette.text.alwaysWhite,
    transition: 'background-color 0.3s ease',
    '&:hover': {
      opacity: 1,
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.text.alwaysWhite, // Override default <a> style
    },
    '&:visited, &.visited': {
      color: theme.palette.text.alwaysWhite, // Override default <a> style
    },
    '&:visited:hover, &.visited:hover': {
      color: theme.palette.text.alwaysWhite, // Override default <a> style
    },
    '&:disabled': {
      backgroundColor: theme.palette.buttons.alwaysPrimary,
      color: theme.palette.text.alwaysWhite,
      opacity: .5,
    }
  },
  '& .ck-cta-button-centered': {
    margin: 'auto'
  }
});

const baseBodyStyles = (theme: ThemeType) => ({
  ...theme.typography.body1,
  ...theme.typography.postStyle,
  wordBreak: "break-word",
  '& pre': {
    ...theme.typography.codeblock
  },
  '& pre.code-block, & pre[data-highlight-language]': {
    backgroundColor: theme.palette.grey[100],
    fontFamily: theme.typography.code.fontFamily,
    display: 'block',
    padding: '8px 8px 8px 36px',
    lineHeight: 1.53,
    fontSize: 13,
    margin: '8px 0',
    overflowX: 'auto',
    position: 'relative',
    tabSize: 2,
    // Override the parent pre rule's `pre-wrap` and the inherited `word-break: break-word`
    // so code scrolls horizontally instead of wrapping (which desyncs the absolute-positioned gutter).
    whiteSpace: 'pre',
    overflowWrap: 'normal',
    // Chrome's UA styles for <details> children break our global `* { box-sizing: inherit }`,
    // leaving `pre` (and its ::before) as content-box inside collapsible sections. That widens
    // the gutter pseudo-element past the pre's padding-left and clips the first code character.
    boxSizing: 'border-box',
  },
  '& pre.code-block::before, & pre[data-highlight-language]::before': {
    content: 'attr(data-gutter)',
    position: 'absolute',
    backgroundColor: theme.palette.grey[200],
    left: 0,
    top: 0,
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    padding: 8,
    color: theme.palette.grey[600],
    whiteSpace: 'pre-wrap',
    textAlign: 'right',
    minWidth: 25,
  },
  '& span.math-tex': {
    display: "inline-block",
  },
  '& code': {
    ...theme.typography.code
  },
  '& .code-token-comment': {
    color: theme.palette.lexicalEditor.codeHighlight.tokenComment,
  },
  '& .code-token-deleted': {
    borderImage: theme.palette.lexicalEditor.codeHighlight.tokenDeleted,
  },
  '& .code-token-inserted': {
    borderImage: theme.palette.lexicalEditor.codeHighlight.tokenInserted,
  },
  '& .code-token-unchanged': {
    borderImage: theme.palette.lexicalEditor.codeHighlight.tokenUnchanged,
  },
  '& .code-token-punctuation': {
    color: theme.palette.lexicalEditor.codeHighlight.tokenPunctuation,
  },
  '& .code-token-property': {
    color: theme.palette.lexicalEditor.codeHighlight.tokenProperty,
  },
  '& .code-token-selector': {
    color: theme.palette.lexicalEditor.codeHighlight.tokenSelector,
  },
  '& .code-token-operator': {
    color: theme.palette.lexicalEditor.codeHighlight.tokenOperator,
  },
  '& .code-token-attr': {
    color: theme.palette.lexicalEditor.codeHighlight.tokenAttr,
  },
  '& .code-token-variable': {
    color: theme.palette.lexicalEditor.codeHighlight.tokenVariable,
  },
  '& .code-token-function': {
    color: theme.palette.lexicalEditor.codeHighlight.tokenFunction,
  },
  '& blockquote': {
    ...theme.typography.blockquote,
    ...theme.typography.body1,
    ...theme.typography.postStyle
  },
  '& li': {
    ...theme.typography.body1,
    ...theme.typography.li,
    ...theme.typography.postStyle
  },
  '& h1': {
    ...theme.typography.display2,
    ...theme.typography.headerStyle,
  },
  // If a post starts with a header, it should still be flush with the top of
  // the container
  '& h1:first-child': {
    marginTop: 0,
    // Otherwise the line height lowers it noticeably
    marginBlockStart: '-3px',
  },
  '& h2': {
    ...theme.typography.display1,
    ...theme.typography.headerStyle,
  },
  '& h2:first-child': {
    marginTop: 0,
    marginBlockStart: '-2px',
  },
  '& h3': {
    ...theme.typography.display0,
    ...theme.typography.headerStyle,
  },
  '& h3:first-child': {
    marginTop: 0,
    marginBlockStart: 0,
  },
  '& h4': {
    ...theme.typography.body1,
    ...theme.typography.postStyle,
    fontWeight:600,
  },
  '& h5': {
  },
  '& h6': {
  },
  '& img': {
    maxWidth: "100%",
    ...(theme.dark && {
      // Override image background color to white (so that transparent isn't
      // black). Necessary because there are a handful of posts with images that
      // have black-on-transparent text in them.
      background: "#ffffff"
    })
  },
  '& sup': {
    verticalAlign: 'baseline',
    top: '-0.6em',
    fontSize: '65%',
    position: 'relative',
    lineHeight: 0,
  },
  '& sub': {
    fontSize: '70%',
    verticalAlign: 'baseline', // We use vertical align baseline to prevent sub-aligned text from changing the line-height, which looks ugly
    position: 'relative',
    top: '0.2em',
    paddingRight: '0.07em'
  },
  '& a, & a:hover, & a:active': {
    color: theme.palette.link.color ?? theme.palette.primary.main,
    '& u': {
      textDecoration: "none"
    }
  },
  '& a:visited, & a.visited': {
    color: theme.palette.link.visited
  },
  '& a:visited:hover, & a.visited:hover, & a:visited:active, & a.visited:active': {},
  '& table': {
    ...tableStyles(theme)
  },
  // CKEditor wraps tables in a figure element
  '& figure.table': {
    width: 'fit-content',
    height: 'fit-content',
  },
  '& td, & th': {
    ...tableCellStyles(theme)
  },
  '& th': {
    ...tableHeadingStyles(theme)
  },
  '& figure': {
    maxWidth: '100%',
    margin: '1em auto',
    textAlign: "center"
  },
  '& figcaption': {
    ...theme.typography.caption,
    ...theme.typography.postStyle
  },
  '& ol > li > ol': {
    listStyle: 'lower-alpha',
  },
  '& ol > li > ol > li > ol': {
    listStyle: 'lower-roman',
  },
  // Hide the marker on Lexical wrapper list items that only contain a nested
  // list (no text content of their own). Without this, the wrapper's marker
  // (e.g. "2.") appears on the same line as the nested list's first item
  // (e.g. "a."), making them look squished together.
  '& .nested-list-item': {
    listStyleType: 'none',
  },
  "& u": {
    textDecoration: "none",
  },
})

export const postBodyStyles = (theme: ThemeType) => {
  return {
    ...baseBodyStyles(theme),
    ...spoilerStyles(theme),
    ...metaculusPreviewStyles(theme),
    ...manifoldPreviewStyles(theme),
    ...neuronpediaPreviewStyles(theme),
    ...calendlyPreviewStyles(theme),
    ...strawpollPreviewStyles(theme),
    ...metaforecastPreviewStyles(theme),
    ...owidPreviewStyles(theme),
    ...estimakerPreviewStyles(theme),
    ...viewpointsPreviewStyles(theme),
    ...youtubePreviewStyles(theme),
    ...lwartifactsPreviewStyles(theme),
    ...footnoteStyles(theme),
    ...collapsibleSectionStyles(theme),
    ...llmContentBlockStyles(theme),
    ...conditionallyVisibleBlockStyles(theme),
    ...ctaButtonStyles(theme),
    // Used for R:A-Z imports as well as markdown-it-footnotes
    '& .footnotes': {
      marginTop: 40,
      fontSize: '0.9em',
      '& li': {
        fontSize: '0.9em', // Overwriting default size setting for list items
        lineHeight: '21.06px',
      },
      paddingTop: 40,
      borderTop: theme.palette.border.normal,
      '& sup': {
        marginRight: 10,
      },
      '& ol': {
        marginBlockStart: '1em',
        paddingInlineStart: 0,
        marginInlineStart: '1em'
      },
      '& blockquote': {
        fontSize: '0.9em',
        lineHeight: '1.5em',
        padding: 1,
        paddingLeft: 3,
        marginTop: -10,
      },
    },
    // Hiding the footnote-separator that markdown-it adds by default
    '& .footnotes-sep': {
      display: 'none'
    },
    '& hr': {
      ...hrStyles(theme),
    }
  }
}

export const commentBodyStyles = (theme: ThemeType, dontIncludePointerEvents?: boolean) => {
  // DoubleHack Fixme: this awkward phrasing is to make it so existing commentBodyStyles don't change functionality, but we're able to use commentBodyStyles without overwriting the pointer-events of child objects.

  const pointerEvents = dontIncludePointerEvents ?
    {} :
    {
      pointerEvents: 'none',
      '& > *': {
        pointerEvents: 'auto',
      },
    }

  const commentBodyStyles = {
    marginTop: ".5em",
    marginBottom: ".25em",
    wordBreak: "break-word",
    ...isIfAnyoneBuildsItFrontPage({
      color: theme.palette.text.bannerAdOverlay,
    }),
    ...theme.typography.body2,
    ...theme.typography.commentStyle,

    '& .footnotes': {
      marginTop: 0,
      paddingTop: 8,
      paddingLeft: 16,
      marginBottom: 0
    },

    '& blockquote': {
      ...theme.typography.commentBlockquote,
      ...theme.typography.body2,
      ...theme.typography.commentStyle,
      ...isIfAnyoneBuildsItFrontPage({
        color: theme.palette.text.bannerAdOverlay,
      }),
    },
    '& li': {
      ...theme.typography.body2,
      ...theme.typography.commentStyle,
      ...isIfAnyoneBuildsItFrontPage({
        color: theme.palette.text.bannerAdOverlay,
      }),
    },
    '& h1, & h2, & h3': {
      ...theme.typography.commentHeader,
      ...theme.typography.commentStyle
    },
    // spoiler styles
    // HACK FIXME: Playing with pointer events is a horrible idea in general, and probably also in this context
    // but it's the only way I was able to make this weird stuff work.
    ...pointerEvents,
    '& > *:hover ~ .spoiler': {
      color: theme.palette.panelBackground.spoilerBlock,
    },
    '& > *:hover ~ .spoiler:before': {
      content: '"spoiler (hover/select to reveal)"',
      color: theme.palette.text.spoilerBlockNotice,
    },
    '& hr': {
      marginTop: 12,
      marginBottom: 12
    },
  }
  return commentBodyStyles;
}

// FIXME: Emails currently don't use this, because the expectations around font size and
// typography are very different in an email. But some subset of these styles should
// actually be applied, eg spoiler-tag handling, even though font selection shouldn't
// be.
export const emailBodyStyles = baseBodyStyles

export const smallPostStyles = (theme: ThemeType) => {
  return {
    ...theme.typography.body2,
    fontSize: 16.64,
    lineHeight: "22.75px",
    ...theme.typography.postStyle,
    '& blockquote': {
      ...theme.typography.body2,
      ...theme.typography.postStyle
    },
    '& ul': {
      paddingInlineStart: 30
    },
    '& li': {
      ...theme.typography.body2,
      ...theme.typography.postStyle,
      fontSize: 16.64,
      lineHeight: "23.4px",
    }
  };
}

export const pBodyStyle = (_theme: ThemeType) => ({
  marginTop: "1em",
  marginBottom: "1em",
  '&:first-child': {
    marginTop: 0,
  },
  'style~&': {
    marginTop: 0,
  },
  '&:last-child': {
    marginBottom: 0,
  }
});

export const ckEditorStyles = (theme: ThemeType) => {
  return {
    '& .ck': {
      '& blockquote': {
        fontStyle: "unset",
        ...theme.typography.blockquote,
        '& p': {
          ...pBodyStyle(theme),
        },
      },
      '--ck-spacing-standard': `8px`,
      '&.ck-content': {
        // As of v46, ckeditor applies some default styles via these css variables.
        // We have our own styles and don't want them, so just set them back to initial.
        '--ck-content-font-family': 'initial',
        '--ck-content-font-size': 'initial',
        '--ck-content-font-color': 'initial',
        '--ck-content-line-height': 'initial',
        '--ck-content-word-break': 'initial',

        marginLeft: -8,
        '--ck-focus-outer-shadow-geometry': "none",
        '--ck-focus-ring': theme.palette.border.transparent,
        '--ck-focus-outer-shadow': "none",
        '--ck-inner-shadow': "none",
        '& p': {
          marginTop: "1em",
          marginBottom: "1em",
          '&:first-child': {
            marginTop: 0,
          }
        },
        '& .table table': {
          ...tableStyles(theme)
        },
        '& .table table td, & .table table th': {
          ...tableCellStyles(theme)
        },
        '& .table table th': {
          ...tableHeadingStyles(theme)
        },
        '& .ck-editor__editable.ck-blurred .ck-widget.ck-widget_selected, .ck-editor__editable.ck-blurred .ck-widget.ck-widget_selected': {
          outline: "none"
        },
        '& .image>figcaption': {
          ...theme.typography.caption,
          backgroundColor: "unset",
        },
        '& hr': {
          ...hrStyles(theme)
        },
        '& .footnote-reference': {
          scrollMarginTop: '100px',
        },
      },
      '& .ck-placeholder:before': {
        whiteSpace: 'break-spaces'
      },
      '&.ck-sidebar, &.ck-presence-list': {
        '& li': {
          // By default ckEditor elements get the styles from postBodyStyles li elements
          marginBottom: 'unset',
          fontFamily: 'unset',
          fontSize: 'unset',
          fontWeight: 'unset',
          lineHeight: 'unset'
        },
        '& .ck-comment:after': {
          display:"none"
        },
        '& .ck-annotation__info-name, & .ck-annotation__info-time, & .ck-comment__input, & .ck-thread__comment-count, & .ck-annotation__main p, & .ck-annotation__info-name, & .ck-annotation__info-time, & .ck-presence-list__counter, &.ck-presence-list': {
          ...theme.typography.body2,
          ...theme.typography.commentStyle,
    
          marginTop: 0,
          alignItems: "flex-start",
          marginBottom: 12
        },
        '&.ck-presence-list': {
          marginBottom: 32,
          '--ck-user-avatar-size': '20px',
          '& .ck-user': {
            marginTop: 0
          }
        },
        '& .ck-thread__comment-count': {
          paddingLeft: 16,
          color: theme.palette.grey[600],
          margin: 0,
          paddingBottom: ".5em",
          '&:before': {
            content: '"\\25B6"'
          }
        },
        '& .ck-comment__input': {
          paddingLeft: 16
        },
        '& .ck-annotation__main, & .ck-comment__input, & .ck-thread__input': {
          width : "100%"
        },
        '& .ck-comment__wrapper': {
          borderTop: theme.palette.border.slightlyFaint,
        },
        '& .ck-annotation__info-name, & .ck-annotation__info-time': {
          color: theme.palette.grey[600],
          fontSize: 13
        },
        '& .ck-annotation__user, & .ck-thread__user': {
          display: "none"
        },
        '--ck-color-comment-count': theme.palette.primary.main,
      },
      
      "--ck-color-base-background": theme.palette.editor.commentPanelBackground,
      "--ck-color-annotation-wrapper-background": theme.palette.editor.commentPanelBackground,
      "--ck-color-comment-background": theme.palette.editor.sideCommentEditorBackground,
      "--ck-color-comment-marker": theme.palette.editor.commentMarker,
      "--ck-color-comment-marker-active": theme.palette.editor.commentMarkerActive,
      '--ck-color-widget-editable-focus-background': theme.palette.panelBackground.default,
      "--ck-comment-content-font-color": theme.palette.text.maxIntensity,
    }
  }
}

export const editorStyles = (theme: ThemeType) => ({
    // Using '*' selectors is a bit dangerous, as is using '!important'
    // This is necessary to catch spoiler-selectors on 'code' elemenents, as implemented in draft-js, 
    // which involved nested spans with manually set style attributes, which can't be overwritten except via 'important'
    //
    // This selector isn't necessary on rendered posts/comments, just the draft-js editor.
    // To minimize potential damage from */important it's only applied here.
    '& .spoiler:not(:hover) *': {
      backgroundColor: `${theme.palette.panelBackground.spoilerBlock} !important`
    },
})
